import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Client } from 'src/providers/client/client';

import { DIRECT_BOOKING_PLATFORM } from 'src/shared/constants/listing-platforms';

import { firstValueFrom } from 'rxjs';

import { environment } from 'src/environments/environment';

const BASE_URL = environment.pmsites_url;

export interface WebsiteConfig {
  id: number;
  property_name: string;
  property_address: string;
  site_url: string;
  editor_url: string;
  custom_domain: string;
  // The address's tidy-tag color, same source the schedule weekly view uses.
  color: string;
  // Mirrors the address's tidy address-listing: 'listed' means the direct
  // booking site is published and guests can see it. Toggled per address from
  // the site detail page, and surfaced here as the Status column.
  is_published: boolean;
}

// One direct booking site per address, reachable from both the Direct Booking
// sites page and the tidy rows of the Listings table — so both build their
// WebsiteConfig here rather than each growing its own copy.
@Injectable({
  providedIn: 'root'
})
export class DirectBookingSitesService {
  private readonly http = inject(HttpClient);
  private readonly client = inject(Client);

  buildConfigs(addresses: any[]): Promise<WebsiteConfig[]> {
    return Promise.all(addresses.map(address => this.buildConfig(address)));
  }

  async buildConfig(address: any): Promise<WebsiteConfig> {
    const [siteConfig, listings] = await Promise.all([
      this.fetchSiteConfig(address.id),
      // Publish state lives on the address's tidy address-listing, so it needs
      // its own per-address read alongside the PMSites site config.
      this.fetchListings(address.id),
    ]);
    return {
      ...this.buildLocalConfig(address),
      custom_domain: siteConfig?.customDomain || '',
      is_published: this.isPublished(listings),
    };
  }

  // Everything derivable from the address alone. Used as the fallback when the
  // two live reads can't be made, so a click still opens a usable page.
  buildLocalConfig(address: any): WebsiteConfig {
    const propertyName = address.address_name || address.address1 || `Property ${address.id}`;
    // add_state is the geographic state; address.state is the record status
    // ('active'), which would otherwise leak into the displayed address.
    const cityState = [address.city, address.add_state].filter(Boolean).join(', ');
    const propertyAddress = [address.address1, cityState, address.zip].filter(Boolean).join(', ');
    return {
      id: address.id,
      property_name: propertyName,
      property_address: propertyAddress,
      site_url: `${BASE_URL}/s/site-${address.id}`,
      editor_url: `${BASE_URL}/editor?token=site-${address.id}`,
      custom_domain: '',
      is_published: false,
      color: address.color || '#1E2125',
    };
  }

  // Re-reads only the two server-owned fields, for callers that already hold a
  // config and just need it un-staled (e.g. after the detail page toggles publish).
  async refreshConfig(config: WebsiteConfig): Promise<WebsiteConfig> {
    const [siteConfig, listings] = await Promise.all([
      this.fetchSiteConfig(config.id),
      this.fetchListings(config.id),
    ]);
    return {
      ...config,
      custom_domain: siteConfig?.customDomain || '',
      is_published: this.isPublished(listings),
    };
  }

  // The direct booking site's on/off switch, shared by the site detail page and
  // the tidy row of the Listings modal. Publishing is not a plain status write:
  // an address that never had a tidy listing has nothing to PUT, and some
  // existing ones reject re-listing that way — both fall back to a create, which
  // publishes immediately. Callers should re-read afterwards, since a create
  // yields a new listing id.
  async setPublished(addressId: number, listingId: number | null, published: boolean): Promise<void> {
    if (!published) {
      if (listingId) {
        await this.client.updateAddressListing(listingId, { status: 'unlisted' });
      }
      return;
    }
    if (listingId) {
      try {
        await this.client.updateAddressListing(listingId, { status: 'listed' });
        return;
      } catch (_err) {
        // Fall through to a fresh create.
      }
    }
    await this.client.createAddressListing({ address_id: addressId, platform: DIRECT_BOOKING_PLATFORM });
  }

  // The site detail page's Listed/Paused dropdown, which only holds the address:
  // looks the tidy listing up, then writes what the Listings page's dropdown
  // writes — Listed through the publish path, Paused as a plain `paused` status.
  async setListed(addressId: number, listed: boolean): Promise<void> {
    const listings = await this.client.getAddressListings(addressId);
    const listing = (listings || []).find((row: any) => row.platform === DIRECT_BOOKING_PLATFORM);
    if (listed) {
      await this.setPublished(addressId, listing?.id ?? null, true);
    } else if (listing) {
      await this.client.updateAddressListing(listing.id, { status: 'paused' });
    }
  }

  // Both reads are best-effort: a property still has a site URL and an editor
  // link when PMSites or the listings call is down, so a failure degrades the
  // config rather than dropping the property.
  private async fetchSiteConfig(addressId: number): Promise<any> {
    const response = await firstValueFrom(
      this.http.get<any>(`${BASE_URL}/api/site/site-${addressId}`)
    ).catch(() => null);
    return response?.config;
  }

  private fetchListings(addressId: number): Promise<any[]> {
    return this.client.getAddressListings(addressId).catch(() => []);
  }

  private isPublished(listings: any[]): boolean {
    return (listings || []).some(
      (listing: any) => listing.platform === DIRECT_BOOKING_PLATFORM && listing.status === 'listed'
    );
  }
}
